import React from 'react';
import {Holiday, Person, Week, Weeks} from './TimesheetsCalendarTypes';
import {
	addDays,
	endOfMonth,
	endOfWeek,
	getWeek,
	isAfter,
	isBefore,
	isSameDay,
	isWeekend,
	startOfMonth,
	startOfWeek,
} from 'date-fns';
import {utcToZonedTime} from 'date-fns-tz';
import {CalendarEventType} from './calendar-events/CalendarEventsFetch';
import Util from '../../../shared/util/util';
import * as tracking from '../../../../tracking';
import RevokeGoogleCalendarTokenMutation from '../../../../mutations/revoke_google_calendar_token_mutation';

export const DAY_PREFIX = 'day-';
export const WEEK_PREFIX = 'week-';

export const LOCALSTORAGE_DISMISS_KEY = 'calendar-info-dismiss-count';
export const LOCALSTORAGE_TIMESHEETS_SELECTED_TAB = 'timesheets_selected_tab';

export const onKeyboardEventHandler = (event: React.KeyboardEvent, onEnter: () => void): void => {
	switch (event.key) {
		case 'Enter':
			onEnter();
			break;
		default:
			break;
	}
};

export const getHolidaysOfTheDay = (person: Person, day: Date): Holiday[] => {
	return person.holidayCalendar
		? person.holidayCalendar.holidayCalendarEntries.edges.filter(
				d => d.node.day === day.getDate() && d.node.month === day.getMonth() + 1 && d.node.year === day.getFullYear()
		  )
		: [];
};

export const workingHourForTheDay = (person: Person, day: Date): number => {
	const personStartDate = new Date(person.startDate || person.createdAt);
	const personEndDate = person.endDate ? new Date(person.endDate) : null;

	if (isBefore(day, personStartDate) || (personEndDate && (isSameDay(day, personEndDate) || isAfter(day, personEndDate)))) {
		return 0;
	}

	const workingHours = [
		person.sunday,
		person.monday,
		person.tuesday,
		person.wednesday,
		person.thursday,
		person.friday,
		person.saturday,
	];

	return workingHours[day.getDay()];
};

export const isBetween = (day: Date, first: Date, last: Date): boolean => {
	return (isBefore(day, last) || isSameDay(day, last)) && (isAfter(day, first) || isSameDay(day, first));
};

export const workingHourForTheWeek = (person: Person, lastDayOfWeek: Date): number => {
	const personStartDate = new Date(person.startDate || person.createdAt);
	const personEndDate = person.endDate && new Date(person.endDate);

	let workingHoursArray: number[];
	const startOfWeekDay = startOfWeek(lastDayOfWeek);
	if (
		isBefore(lastDayOfWeek, personStartDate) ||
		(personEndDate && (isSameDay(startOfWeekDay, personEndDate) || isAfter(startOfWeekDay, personEndDate)))
	) {
		return 0; //the person didn't start yet or isn't working there anymore
	} else if (isBetween(personStartDate, startOfWeekDay, lastDayOfWeek)) {
		workingHoursArray = [
			person.sunday,
			person.monday,
			person.tuesday,
			person.wednesday,
			person.thursday,
			person.friday,
			person.saturday,
		];
		const startDay = personStartDate.getDate();
		workingHoursArray = workingHoursArray.map((hours, index) => (index < startDay ? 0 : hours));
	} else if (personEndDate && isBetween(personEndDate, startOfWeekDay, lastDayOfWeek)) {
		workingHoursArray = [
			person.sunday,
			person.monday,
			person.tuesday,
			person.wednesday,
			person.thursday,
			person.friday,
			person.saturday,
		];
		const endDay = personEndDate.getDate();
		workingHoursArray = workingHoursArray.map((hours, index) => (index >= endDay ? 0 : hours));
	} else {
		workingHoursArray = [
			person.sunday,
			person.monday,
			person.tuesday,
			person.wednesday,
			person.thursday,
			person.friday,
			person.saturday,
		];
	}

	return workingHoursArray.reduce((total, sum) => total + sum);
};

const getWeekDateArray = (weekStart: Date, hideWeekend: boolean) => {
	const includedDays = hideWeekend ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5, 6];
	const week: Date[] = [];

	for (let n = 0; n < 7; n++) {
		const day = addDays(new Date(weekStart.getTime()), n);
		if (includedDays.includes(day.getDay())) {
			week.push(day);
		}
	}

	return week;
};

export const getWeeksOfMonth = (currentViewingMonth: Date, hideWeekend: boolean): Weeks => {
	let startDate = startOfWeek(startOfMonth(currentViewingMonth));
	const endDate = endOfWeek(endOfMonth(currentViewingMonth));

	const calendar: Weeks = [];
	while (isBefore(startDate, endDate)) {
		const weekStart = startDate;
		calendar.push({
			days: getWeekDateArray(weekStart, hideWeekend),
		});
		startDate = addDays(startDate, 7);
	}

	return calendar;
};

export const isDaySelected = (selectedDate: string, day: Date): boolean => {
	return selectedDate === DAY_PREFIX + day.getDate() + '-' + day.getMonth();
};

export const isWeekSelected = (selectedDate: string, week: Week): boolean => {
	const day = week.days[0];
	return selectedDate === WEEK_PREFIX + getWeek(day);
};

export const getFirstDayOfWeek = (): number => {
	return startOfWeek(new Date()).getDay();
};

export const getWeekdaysShort = (hideWeekend: boolean): string[] => {
	const days: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const result: string[] = [];
	let day = startOfWeek(new Date());
	for (let n = 0; n < 7; n++) {
		if (!(isWeekend(day) && hideWeekend)) {
			result.push(days[day.getDay()]);
		}
		day = addDays(day, 1);
	}
	return result;
};

export const getStartDate = (event: CalendarEventType | undefined): Date | null => {
	return event?.start && event?.calendarTimeZone ? utcToZonedTime(event.start, event.calendarTimeZone) : null;
};

export const getEndDate = (event: CalendarEventType | undefined): Date | null => {
	return event?.end && event?.calendarTimeZone ? utcToZonedTime(event.end, event.calendarTimeZone) : null;
};

export const revokeTokens = (personId: string): void => {
	tracking.trackEvent('Google Calendar Revoke Tokens');
	localStorage.removeItem(LOCALSTORAGE_TIMESHEETS_SELECTED_TAB);
	localStorage.setItem(LOCALSTORAGE_DISMISS_KEY, '0');
	Util.CommitMutation(RevokeGoogleCalendarTokenMutation, {
		personId: personId,
	});
};
